import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { Box, Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import {
  COUNTRIES_LIST,
  STATE_LIST,
  TIME_ZONE_LIST,
} from "../../../../constants";
import {
  PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES,
  PROVIDER_GROUP_LOCATION_SCHEMA,
  ProviderGroupLocationFormType,
} from "../../../../constants/ProviderLocationFromInitialValues";
import CustomSelect from "../../../CustomSelect";
import Flex from "../../../Flex";
import Input from "../../../Input";
import OutlineCard from "../../../OutlineCard";
import SideSheetDrawer from "../../../SidesheetDrawer";
import Text from "../../../Text";
import WeekTimePicker from "../../../WeekTimePicker";
import DropZone from "../../DropZone";
// import ToggleSwitch from "../../../ToggleSwitch";
import { useQueryClient } from "@tanstack/react-query";
import { isEqual } from "lodash";
import { useParams } from "react-router-dom";
import { useProviderGroupLocationProfile } from "../../../../queries/admin/provider-group/use-provider-group-location-profile";
import { useTenantConfiguration } from "../../../../queries/use-get-tenant-configuration";
import { useLocationAvatar } from "../../../../queries/use-location-change-avatar";
import { useSpecialtiesList } from "../../../../queries/use-specialities-list";
import {
  convertToBase64,
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  unNecessaryBEOfficeHourTrans,
} from "../../../../utils/utils";
import CustomTextArea from "../../../CustomTextArea";
import FormHeadChip from "../../../FormHeadText";
import MaskInput from "../../../MaskInput";
import MaskMultiSelectScroll from "../../../MaskMultiSelectScroll";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useFormScrollIntoView } from "../../../../hooks/use-form-scrollInto-view";

interface LocationsModalProps {
  providerGroupTimezone: string;
  open: boolean;
  handleCloseModal: () => void;
  title?: string;
  onSubmit: (values: ProviderGroupLocationFormType) => Promise<any>;
  preFillValues?: ProviderGroupLocationFormType;
  prefillSpecialtyList?: any;
  shouldGetPrefillList?: boolean;
  locationUuid?: string;
}

const LocationsDrawer = ({
  locationUuid,
  providerGroupTimezone,
  open,
  handleCloseModal,
  title,
  onSubmit,
  prefillSpecialtyList,
  shouldGetPrefillList,
  preFillValues,
}: LocationsModalProps) => {
  const showSnackbar = useSnackbar();

  const params = useParams();
  const [
    isBillingAddressSameToPhysicalAdd,
    setIsBillingAddressSameToPhysicalAdd,
  ] = useState<boolean>(false);
  const [specialtiesPagination, setSpecialtiesPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: specialtiesList, isFetching: isSpecialtiesFetching } =
    useSpecialtiesList({
      xTENANTID: params?.providerGroupId,
      pagination: specialtiesPagination,
    });

  const { data: locationProfile, refetch } = useProviderGroupLocationProfile({
    disable: !locationUuid,
    locationId: locationUuid ?? "",
    xTENANTID: params?.providerGroupId,
  });

  const [isErrorForOfficeHr, setIsErrorForOfficeHr] = useState<boolean>(false);
  const { data: TenantConfigurationData } = useTenantConfiguration({
    enabled: open,
  });
  const client = useQueryClient();
  const { mutateAsync: changeAvatar } = useLocationAvatar();

  const formik = useFormik({
    initialValues: preFillValues ?? {
      ...PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES,
      timezone:
        providerGroupTimezone ?? TenantConfigurationData?.data?.timeZone,
    },
    validationSchema: PROVIDER_GROUP_LOCATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: (v) => {
      if (isErrorForOfficeHr) {
        showSnackbar(getErrorSnackbar("Please select valid office hours"));
        return;
      }

      if (v?.billingAddress) {
        Object.keys(v.billingAddress).forEach((it: any) => {
          if (
            v?.billingAddress !== undefined &&
            (v?.billingAddress as any)?.[it as any] === ""
          )
            delete v.billingAddress;
        });
      }

      if (v?.physicalAddress) {
        Object.keys(v.physicalAddress).forEach((it: any) => {
          if (
            v?.physicalAddress !== undefined &&
            (v?.physicalAddress as any)?.[it as any] === ""
          )
            delete v.physicalAddress;
        });
      }

      onSubmit({
        ...v,
        officeHours: unNecessaryBEOfficeHourTrans(v?.officeHours) as any,
        avatar: title?.includes("Edit") ? "" : v.avatar,
      })
        ?.then((res) => {
          if (
            v.avatar &&
            !v?.avatar?.includes(".amazonaws.com") &&
            title?.includes("Edit")
          ) {
            changeAvatar({
              userid: (preFillValues as any)?.uuid ?? "",
              newavatar: v.avatar,
              xTENANTID: params?.providerGroupId,
            }).then(() => {
              client.invalidateQueries({
                queryKey: ["provider-group-location-profile"],
              });
            });
          }
          showSnackbar(getSuccessSnackbar(res));
          client.invalidateQueries({
            queryKey: ["provider-group-location-list"],
          });
          client.invalidateQueries({
            queryKey: ["provider-group-location-profile"],
          });

          closeAndResetModal();
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    },
  });

  useFormScrollIntoView(formik);

  useEffect(() => {
    setIsBillingAddressSameToPhysicalAdd(
      !!(preFillValues?.billingAddress && preFillValues?.physicalAddress) &&
        isEqual(preFillValues?.physicalAddress, preFillValues?.billingAddress)
    );

    refetch();
  }, [preFillValues]);

  useEffect(() => {
    isBillingAddressSameToPhysicalAdd &&
      formik.setValues((prev) => ({
        ...prev,
        billingAddress:
          prev?.physicalAddress ??
          PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES.billingAddress,
      }));
  }, [formik.values?.physicalAddress]);

  useEffect(() => {
    locationProfile?.avatar &&
      formik.setFieldValue("avatar", locationProfile?.avatar);
  }, [locationProfile?.avatar]);

  function closeAndResetModal() {
    setIsBillingAddressSameToPhysicalAdd(false);
    handleCloseModal();
    formik.resetForm();
  }

  useFormScrollIntoView(formik);

  const specialtiesOptions = useMemo(() => {
    if (shouldGetPrefillList) return (prefillSpecialtyList ?? []) as any[];
    const fetched = (specialtiesList?.data?.content ?? []) as {
      [k: string]: string;
    }[];
    const selected = (formik.values.specialities ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [
    shouldGetPrefillList,
    prefillSpecialtyList,
    specialtiesList,
    formik.values.specialities,
  ]);

  return (
    <SideSheetDrawer
      open={open}
      title={title}
      w="74vw"
      footerActions={
        <Button
          variant="contained"
          type="submit"
          onClick={() => {
            formik.submitForm();
          }}
        >
          Save
        </Button>
      }
      onClose={closeAndResetModal}
    >
      <Grid
        container
        // width={{
        //   sm: "580px",
        //   md: "700px",
        //   lg: "980px",
        // }}
        gap={"6px"}
        borderRadius={"10px"}
      >
        {/* Commented Since Requirement Changed! */}
        {/* {title?.includes("Edit") && (
          <Grid item md={12}>
            <Box>
              <Text>Status</Text>
              <ToggleSwitch
                checked={formik.values.active}
                onChange={(e) =>
                  formik.setFieldValue("active", e.target.checked)
                }
              />
            </Box>
          </Grid>
        )} */}
        <Flex id="tempid" gap={3}>
          <Grid container spacing={2}>
            <Grid item container spacing={2}>
              <Grid item md={6} lg={3}>
                <Input
                  required
                  label="Location Name"
                  textFieldProps={{
                    placeholder: "Enter Location Name",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "name",
                    value: formik.values.name,
                  }}
                  error={formik?.touched?.name && formik.errors?.name}
                />
              </Grid>
              <Grid item xs={2} sm={12} md={6} lg={3}>
                <Input
                  label="Location ID"
                  textFieldProps={{
                    placeholder: "Enter Location ID",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "locationId",
                    value: formik.values.locationId,
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={12} md={6} lg={3}>
                <MaskMultiSelectScroll
                  placeholder="Select"
                  label="Speciality Type"
                  options={specialtiesOptions}
                  optionWithCheckbox={true}
                  value={formik.values.specialities}
                  onChange={(values) => {
                    formik.setFieldValue("specialities", values);
                  }}
                  pagination={specialtiesPagination}
                  setPagination={setSpecialtiesPagination}
                  totalElements={specialtiesList?.data?.page?.totalElements}
                  loading={isSpecialtiesFetching}
                />
              </Grid>
              <Grid item lg={3} md={6}>
                <MaskInput
                  mask={"(999) 999-9999"}
                  label="Contact Number"
                  name="contact"
                  error={formik.errors.contact}
                  placeholder="Enter Contact Number"
                  value={formik.values.contact}
                  onChange={(e) =>
                    formik.setFieldValue("contact", e.target.value)
                  }
                />
              </Grid>
              <Grid item lg={3} md={6}>
                <Input
                  label="Email"
                  textFieldProps={{
                    placeholder: "Enter Email",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "email",
                    value: formik.values.email,
                  }}
                  error={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={6} sm={12} md={6} lg={3}>
                <Input
                  label="Fax ID"
                  textFieldProps={{
                    placeholder: "Enter Fax ID",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "fax",
                    value: formik.values.fax,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomSelect
                  required
                  name="timezone"
                  label="Time Zone"
                  value={formik.values.timezone}
                  options={TIME_ZONE_LIST}
                  onChange={(val) => formik.setFieldValue("timezone", val)}
                  handleBlur={formik.handleBlur}
                  error={formik?.touched?.timezone && formik.errors?.timezone}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextArea
                  label="Information"
                  id="outlined-multiline-static"
                  rows={3}
                  variant="outlined"
                  name="note"
                  placeholder="Enter Information"
                  onBlur={formik.handleBlur}
                  value={formik.values?.note}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </Grid>
        </Flex>

        <Grid marginTop={2} container>
          <Grid item xs={12}>
            <Text variant="h4" fontWeight={"medium"} mb={"10px"}>
              Physical Address
            </Text>
          </Grid>
          <Grid item xs={12}>
            <OutlineCard>
              <Grid container spacing={2} alignItems={"center"} padding={1}>
                <Grid item xs={6}>
                  <Input
                    required
                    label="Address Line 1"
                    textFieldProps={{
                      placeholder: "Address Line 1",
                      onChange: formik.handleChange,
                      onBlur: formik.handleBlur,
                      name: "physicalAddress.line1",
                      value: formik.values.physicalAddress?.line1 ?? "",
                    }}
                    error={
                      (formik?.touched?.physicalAddress as any)?.line1 &&
                      (formik.errors?.physicalAddress as any)?.line1
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    required
                    label="Address Line 2"
                    textFieldProps={{
                      placeholder: "Address Line 2",
                      onChange: formik.handleChange,
                      onBlur: formik.handleBlur,
                      name: "physicalAddress.line2",
                      value: formik.values.physicalAddress?.line2 ?? "",
                    }}
                    error={
                      (formik?.touched?.physicalAddress as any)?.line2 &&
                      (formik.errors?.physicalAddress as any)?.line2
                    }
                  />
                </Grid>
                <Grid item xs={3} sm={6} md={3} lg={3}>
                  <Input
                    required
                    label="City"
                    textFieldProps={{
                      placeholder: "City",
                      onChange: formik.handleChange,
                      onBlur: formik.handleBlur,
                      name: "physicalAddress.city",
                      value: formik.values.physicalAddress?.city ?? "",
                    }}
                    error={
                      (formik?.touched?.physicalAddress as any)?.city &&
                      (formik.errors?.physicalAddress as any)?.city
                    }
                  />
                </Grid>
                <Grid item xs={3} sm={6} md={3} lg={3}>
                  <CustomSelect
                    required
                    label="State"
                    value={formik.values.physicalAddress?.state ?? ""}
                    options={STATE_LIST}
                    onChange={(val) =>
                      formik.setFieldValue("physicalAddress.state", val)
                    }
                    handleBlur={formik.handleBlur}
                    name="physicalAddress.state"
                    placeholder="Select"
                    error={
                      (formik?.touched?.physicalAddress as any)?.state &&
                      (formik.errors?.physicalAddress as any)?.state
                    }
                  />
                </Grid>
                <Grid item xs={3} sm={6} md={3} lg={3}>
                  <CustomSelect
                    required
                    label="Country"
                    placeholder="Country"
                    disableSortOptions
                    options={COUNTRIES_LIST}
                    value={formik.values?.physicalAddress?.country ?? ""}
                    onChange={(v) =>
                      formik.setFieldValue("physicalAddress.country", v)
                    }
                    handleBlur={formik.handleBlur}
                    name="physicalAddress.country"
                    error={
                      (formik?.touched?.physicalAddress as any)?.country &&
                      (formik.errors?.physicalAddress as any)?.country
                    }
                  />
                </Grid>
                <Grid item xs={3} sm={6} md={3} lg={3}>
                  <Input
                    required
                    label="ZIP Code"
                    textFieldProps={{
                      name: "physicalAddress.zipcode",
                      value: (() => {
                        const d = (
                          (formik.values.physicalAddress as any)?.zipcode ?? ""
                        )
                          .replace(/\D/g, "")
                          .slice(0, 9);
                        return d.length > 5
                          ? `${d.slice(0, 5)}-${d.slice(5)}`
                          : d;
                      })(),
                      onChange: (e) => {
                        const digits = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 9);
                        const val =
                          digits.length > 5
                            ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                            : digits;
                        formik.setFieldValue("physicalAddress.zipcode", val);
                      },
                      onBlur: formik.handleBlur,
                      inputProps: { maxLength: 10 },
                    }}
                    error={
                      (formik?.touched?.physicalAddress as any)?.zipcode &&
                      (formik.errors?.physicalAddress as any)?.zipcode
                    }
                  />
                </Grid>
              </Grid>
            </OutlineCard>
          </Grid>
          <Grid container sx={{ marginTop: "16px" }}>
            <Grid item xs={12} display={"flex"} alignItems={"center"} gap={2}>
              <FormHeadChip>Billing Address</FormHeadChip>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    checked={isBillingAddressSameToPhysicalAdd}
                    onChange={(e) => {
                      /** if checked is true
                       * set copied the physical to billing add
                       * if checked is false
                       * set initial billing
                       */
                      formik.setValues((prev) => ({
                        ...prev,
                        billingAddress: e.target.checked
                          ? prev.physicalAddress
                          : PROVIDER_GROUP_LOCATION_FORM_INITIAL_VALUES?.billingAddress,
                      }));

                      setIsBillingAddressSameToPhysicalAdd(e.target.checked);
                    }}
                  />
                }
                label={<Text>Same as Physical Address</Text>}
                labelPlacement="end"
                sx={{ marginLeft: "10px" }}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlineCard>
                <Grid container spacing={2} alignItems={"center"} padding={1}>
                  <Grid item xs={6}>
                    <Input
                      label="Address Line 1"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        placeholder: "Address Line 1",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "billingAddress.line1",
                        value: formik.values?.billingAddress?.line1 ?? "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      label="Address Line 2"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        placeholder: "Address Line 2",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "billingAddress.line2",
                        value: formik.values.billingAddress?.line2 ?? "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} sm={6} md={3} lg={3}>
                    <Input
                      label="City"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        placeholder: "City",
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        name: "billingAddress.city",
                        value: formik.values.billingAddress?.city ?? "",
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} sm={6} md={3} lg={3}>
                    <CustomSelect
                      readOnly={isBillingAddressSameToPhysicalAdd}
                      label="State"
                      value={formik.values.billingAddress?.state}
                      options={STATE_LIST}
                      onChange={(val) =>
                        formik.setFieldValue("billingAddress.state", val)
                      }
                      placeholder="Select"
                    />
                  </Grid>
                  <Grid item xs={3} sm={6} md={3} lg={3}>
                    <CustomSelect
                      label="Country"
                      readOnly={isBillingAddressSameToPhysicalAdd}
                      placeholder="Country"
                      disableSortOptions
                      options={COUNTRIES_LIST}
                      value={formik.values?.billingAddress?.country}
                      onChange={(v) =>
                        formik.setFieldValue("billingAddress.country", v)
                      }
                    />
                  </Grid>
                  <Grid item xs={3} sm={6} md={3} lg={3}>
                    <Input
                      label="ZIP Code"
                      textFieldProps={{
                        disabled: isBillingAddressSameToPhysicalAdd,
                        name: "billingAddress.zipcode",
                        value: (() => {
                          const d = (
                            formik.values.billingAddress?.zipcode ?? ""
                          )
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          return d.length > 5
                            ? `${d.slice(0, 5)}-${d.slice(5)}`
                            : d;
                        })(),
                        onChange: (e) => {
                          const digits = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          const val =
                            digits.length > 5
                              ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                              : digits;
                          formik.setFieldValue("billingAddress.zipcode", val);
                        },
                        onBlur: formik.handleBlur,
                        inputProps: { maxLength: 10 },
                      }}
                      error={
                        (formik?.touched?.billingAddress as any)?.zipcode &&
                        (formik.errors?.billingAddress as any)?.zipcode
                      }
                    />
                  </Grid>
                </Grid>
              </OutlineCard>
            </Grid>
          </Grid>

          <Grid item xs={4} mt={"20px"}>
            <Box marginBottom={"20px"}>
              <DropZone
                supportedFile=".jpg,.jpeg,.png,.svg"
                value={formik.values.avatar}
                labelComponent={<FormHeadChip>Logo</FormHeadChip>}
                content="Upload Location Logo"
                height={"190px"}
                backgroundColor={"primary.light"}
                uploadIcon={
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "4rem" }} />
                }
                onClick={async (files) => {
                  if (files) {
                    const base64File = await convertToBase64(files[0]);
                    formik.setFieldValue("avatar", base64File);
                  } else formik.setFieldValue("avatar", null);
                }}
                accept=".jpg,.jpeg,.png,.svg"
              />
            </Box>
          </Grid>
        </Grid>

        <Box width={"100%"}>
          <FormHeadChip mb={"10px"}>Location Working Hours</FormHeadChip>
          <OutlineCard>
            <Box sx={{ padding: 1 }}>
              <WeekTimePicker
                setIsError={setIsErrorForOfficeHr}
                pickedTimes={formik.values.officeHours}
                onChange={(value) => formik.setFieldValue("officeHours", value)}
              />
            </Box>
          </OutlineCard>
        </Box>
      </Grid>
    </SideSheetDrawer>
  );
};

export default LocationsDrawer;
